// PLAT-3371 - thread mode, derived rather than stored.
//
// The mode MUST be a pure derivation over server-owned fields: the indicator
// reflects Meta's CONFIRMED state, never the mode the agent clicked. There is
// deliberately no setter and no "intended mode" argument, so a failed change
// re-resolves to the previous mode for free.
//
// NO IMPORTS, deliberately: this module must be executable by jest in a repo
// with no test setup of its own.

export const THREAD_MODE_LIVE_CHAT = "LIVE_CHAT"
export const THREAD_MODE_BOT = "BOT"

// Declared shape is `{ key, label, icon, memberLookupField }` - the identifier
// is `key`, not `mode` (radiate `AppIntegration.graphql: InboxThreadMode`).
//
// NO CONSTANT FOR ANY PARTICULAR EXTENSION MODE. An extra mode is just a
// declared `key`, so a second integration needs no change here.

/**
 * Declared extra modes, normalised to keys.
 *
 * Every absent or malformed shape MUST default to the empty list: a channel
 * that declares nothing shows no extra affordance, and this is a shared inbox.
 */
export function declaredModeIds(declaredModes) {
  if (!Array.isArray(declaredModes)) {
    return []
  }
  const seen = []
  return declaredModes
    .map((declared) => {
      if (typeof declared === "string") {
        return declared
      }
      return declared && typeof declared.key === "string" ? declared.key : null
    })
    .filter((mode) => {
      // De-duplicate HERE, not in callers: every consumer renders one affordance
      // per entry, so a repeated key is a repeated button.
      if (!mode || seen.indexOf(mode) !== -1) {
        return false
      }
      seen.push(mode)
      return true
    })
}

/**
 * Is a declared extra mode available on this thread?
 */
export function isModeDeclared(mode, declaredModes) {
  return declaredModeIds(declaredModes).indexOf(mode) !== -1
}

/**
 * The full declaration for a mode key, so a render site can use the extension's
 * own `label` and `icon` instead of inventing copy for someone else's mode.
 * Returns null for the two built-ins and for anything undeclared.
 */
export function declaredMode(mode, declaredModes) {
  if (!Array.isArray(declaredModes)) {
    return null
  }
  return declaredModes.find((declared) => declared && declared.key === mode) || null
}

/**
 * The current thread mode, derived from server state alone.
 *
 * A DECLARED MODE WINS over `liveChat`; they are not peers. `activeThreadMode`
 * is resolved server-side from the mode's `memberLookupField` and is only
 * written once a third party confirmed the control change, whereas `liveChat` is
 * a local flag about whether our bot answers. While a declared mode holds the
 * thread the agent usually cannot send, so an active live-chat composer would be
 * a lie in the direction that loses a customer message.
 *
 * ACCEPTED TRADE, ruled by Roy 2026-08-28 GMT+8: there is NO restore-previous.
 * After a handover back, the agent is always in live chat, never returned to bot
 * mode. Do not add a previous-mode field to "fix" it.
 */
export function deriveThreadMode({ liveChat, activeThreadMode, declaredModes } = {}) {
  // The declaration gate MUST beat the resolved key: an extension can be
  // uninstalled while its marker survives, and an undeclared channel shown a
  // mode it has no affordance to leave is the leak this prevents.
  if (typeof activeThreadMode === "string"
    && activeThreadMode
    && isModeDeclared(activeThreadMode, declaredModes)) {
    return activeThreadMode
  }
  return liveChat ? THREAD_MODE_LIVE_CHAT : THREAD_MODE_BOT
}

// THE WIRE VOCABULARY IS NOT THIS FILE'S. The built-in ids are inbox-internal;
// the extension endpoint matches its own `bot` / `live_chat`. Sending
// `LIVE_CHAT` yields an InvalidInput that reads like a Meta failure.
//
// A DECLARED key MUST pass through verbatim - it goes back to the extension that
// declared it, so it must not be case-folded to match the built-ins.
const WIRE_MODES = {
  [THREAD_MODE_LIVE_CHAT]: "live_chat",
  [THREAD_MODE_BOT]: "bot",
}

export function threadModeToWire(mode) {
  return WIRE_MODES[mode] || mode
}

/**
 * The extra modes declared on THIS thread's channel.
 *
 * The host type MUST be Thread, not UserScope: UserScope collects across every
 * channel the agent can see, so a declaration on one channel would surface on
 * another channel's thread. Nor under `inputActions` - that resolver drops an
 * integration whose `inbox.inputActions` is empty, silently losing a mode-only
 * declaration.
 *
 * Re-normalises even though the API already does: Relay can deliver partial or
 * masked data, and the cost of being wrong is an affordance on a channel that
 * never asked for one.
 */
export function declaredModesForThread(thread) {
  return declaredModeIds(thread && thread.additionalThreadModes)
}

/**
 * The full declarations for this thread, so a render site can reach an
 * extension's own `label` and `icon`.
 */
export function declaredModeListForThread(thread) {
  const declared = thread && thread.additionalThreadModes
  return Array.isArray(declared) ? declared : []
}

/**
 * The modes an agent may select on this thread, in display order.
 *
 * DECLARED EXTRAS FIRST, THEN LIVE CHAT, THEN BOT - Roy's design, ruled
 * 2026-09-01 GMT+8. Not an accident of appending, and not to be "restored" to
 * built-ins-first.
 *
 * This is the ONLY definition of display order, so it also sets DOM order and
 * therefore keyboard traversal.
 */
export function selectableThreadModes(declaredModes) {
  const extras = declaredModeIds(declaredModes)
    .filter((mode) => mode !== THREAD_MODE_LIVE_CHAT && mode !== THREAD_MODE_BOT)
  return [...extras, THREAD_MODE_LIVE_CHAT, THREAD_MODE_BOT]
}

// PLAT-3418 - the N-way toggle's rules. Presentation-free so they are testable
// without mounting a component; the toggle renders what it is given.

// The reason a segment is unusable IS the value - a single `disabled` boolean
// carried in-flight, role and "this is the active one" and rendered all three
// identically.
export const SEGMENT_ACTIVE = "active"
export const SEGMENT_AVAILABLE = "available"
export const SEGMENT_CHANNEL_DISABLED = "channel-disabled"
export const SEGMENT_UNREACHABLE = "unreachable"

// Transition kinds. Roy's rules, encoded once.
export const TRANSITION_NONE = "none"
export const TRANSITION_TOGGLE_LIVE_CHAT = "toggle-live-chat"
export const TRANSITION_DISPATCH = "dispatch"

/**
 * Cap on an extension-authored mode label.
 *
 * KEPT AT 24 BY RULING - Roy, 2026-09-01 GMT+8: "24-char cap is okay." It looks
 * like a bug: since the icons-only redesign the toggle has no label text, so the
 * cap buys no layout protection and only truncates the tooltip and `aria-label`,
 * which are now the only ways to learn a mode's name. That truncation is
 * ACCEPTED. Relaxing it needs a ruling, not a tidy-up.
 *
 * TOGGLE ONLY. The bubble's 18 (`MAX_SENDER_LABEL_LENGTH`) and the preview's 16
 * (API `MAX_SENDER_TAG_LENGTH`) are separate decisions - do not unify them.
 */
export const MAX_MODE_LABEL_LENGTH = 24

/**
 * NOT escaped here: React escapes text children, so escaping would double-encode
 * and the agent would read `&amp;`. The boundary is the render site - never put
 * this in dangerouslySetInnerHTML, an href or a style.
 */
export function capModeLabel(label) {
  if (typeof label !== "string") {
    return ""
  }
  const trimmed = label.trim()
  if (trimmed.length <= MAX_MODE_LABEL_LENGTH) {
    return trimmed
  }
  return trimmed.slice(0, MAX_MODE_LABEL_LENGTH - 1) + "…"
}

/**
 * ONLY an explicit `false` disables; absent, null and undefined are ENABLED.
 *
 * The asymmetry is the point: "enabled but actually off" surfaces as an error the
 * agent can act on, while "disabled but actually available" silently denies a
 * capability and looks intentional.
 */
export function modeIsChannelEnabled(declaration) {
  return !(declaration && declaration.enabled === false)
}

/**
 * Which transition a segment implies. Roy's rules, deliberately NOT symmetric:
 *
 *   BOT       -> LIVE_CHAT : local flag (toggleLiveChat true)
 *   LIVE_CHAT -> BOT       : local flag (toggleLiveChat false)
 *   extra     -> LIVE_CHAT : DISPATCH - Meta must agree before anything moves
 *   anything  -> extra     : DISPATCH
 *   extra     -> BOT       : NOT REACHABLE. Taking a thread back always lands
 *                            the agent in live chat; bot is one further step
 *                            they take deliberately.
 */
export function transitionFor({ fromMode, toKey, declaredKeys = [] }) {
  const isExtra = (mode) => declaredKeys.indexOf(mode) !== -1
    && mode !== THREAD_MODE_LIVE_CHAT && mode !== THREAD_MODE_BOT

  if (fromMode === toKey) {
    return { kind: TRANSITION_NONE, reason: "already-active" }
  }
  if (isExtra(toKey)) {
    return { kind: TRANSITION_DISPATCH, target: toKey }
  }
  if (toKey === THREAD_MODE_LIVE_CHAT) {
    return isExtra(fromMode)
      ? { kind: TRANSITION_DISPATCH, target: THREAD_MODE_LIVE_CHAT }
      : { kind: TRANSITION_TOGGLE_LIVE_CHAT, target: true }
  }
  if (toKey === THREAD_MODE_BOT) {
    return isExtra(fromMode)
      ? { kind: TRANSITION_NONE, reason: "bot-not-reachable-from-extra" }
      : { kind: TRANSITION_TOGGLE_LIVE_CHAT, target: false }
  }
  return { kind: TRANSITION_NONE, reason: "unknown-mode" }
}

/**
 * The segments in display order, each with its state and transition.
 *
 * N segments, never three: built on `selectableThreadModes` so display order
 * lives in one place. Built-ins carry no declaration and are therefore always
 * channel-enabled - only a DECLARED mode can be turned off per channel.
 */
export function threadModeSegments({ thread, threadMode }) {
  const declarations = declaredModeListForThread(thread)
  const declaredKeys = declaredModesForThread(thread)
  const byKey = {}
  declarations.forEach((d) => {
    if (d && typeof d.key === "string") {
      byKey[d.key] = d
    }
  })

  return selectableThreadModes(declarations).map((key) => {
    const declaration = byKey[key] || null
    const isBuiltIn = key === THREAD_MODE_LIVE_CHAT || key === THREAD_MODE_BOT
    const transition = transitionFor({ fromMode: threadMode, toKey: key, declaredKeys })

    let state
    if (key === threadMode) {
      state = SEGMENT_ACTIVE
    } else if (!modeIsChannelEnabled(declaration)) {
      state = SEGMENT_CHANNEL_DISABLED
    } else if (transition.kind === TRANSITION_NONE) {
      // Reachable in principle, but not from HERE. Today that is only
      // bot-from-an-extra-mode.
      state = SEGMENT_UNREACHABLE
    } else {
      state = SEGMENT_AVAILABLE
    }

    return {
      key,
      isBuiltIn,
      // Built-in labels are ours to translate; a declared label belongs to the
      // extension and is capped, never invented.
      label: isBuiltIn ? null : capModeLabel((declaration && declaration.label) || key),
      iconName: declaration ? declaration.icon : null,
      state,
      transition,
    }
  })
}
